/**
 * QuickRemittanceForm.js — "Modo rápido" remittance form.
 *
 * Visual rediseño 2026-06: layout estilo "Enviar Remesa" teal — same
 * business logic (rates, fund check, submit). The only structural change
 * is the layout/typography to match the user-provided mockup.
 *
 *   1. Tipo de Remesa (CUP / MLC / USD)
 *   2. Cantidad a Enviar (UYU) ↔ Cantidad a Recibir (autocomputed)
 *   3. Teléfono para notificación
 *   4. Tarjeta temporal (formato dddd-dddd-dddd-dddd)
 *
 * El backend valida y vuelve a calcular en el server side (sales finance v3),
 * por lo que estos campos del frontend son meramente informativos.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, AlertTriangle, Wallet, Phone, CreditCard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import { listRates, createOperation } from '@/services/remittanceApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEST_CURRENCIES = [
  { key: 'CUP', label: 'CUP (Peso cubano)' },
  { key: 'MLC', label: 'MLC' },
  { key: 'USD', label: 'USD (clásica)' },
];

const onlyDigits = (s) => (s || '').replace(/\D+/g, '');

// Format card as dddd-dddd-dddd-dddd while typing.
const formatCard = (raw) => {
  const digits = onlyDigits(raw).slice(0, 16);
  return digits.replace(/(.{4})(?=.)/g, '$1-');
};

const QuickRemittanceForm = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [rates, setRates] = useState([]);
  const [fund, setFund] = useState(null);
  const [form, setForm] = useState({
    currency_to: '',
    amount_uyu: '',
    card_number: '',
    phone: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let timer = null;
    const refreshRates = async () => {
      try {
        const r = await listRates(getAuthHeaders);
        if (!cancelled) setRates(r.rates || []);
      } catch {
        if (!cancelled) setRates([]);
      }
    };
    (async () => {
      await refreshRates();
      if (!isAdmin) {
        try {
          const f = await axios.get(`${API}/gestor/me/remittance-fund`, { headers: getAuthHeaders() });
          if (!cancelled) setFund(f.data);
        } catch {
          if (!cancelled) setFund(null);
        }
      }
    })();
    // Poll every 30s so the preview always matches the latest "Zona de Cambios"
    // (Iter 64 fix: rates derived from usdt_rates_config in real time).
    timer = setInterval(refreshRates, 30_000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [getAuthHeaders, isAdmin, user?.id]);

  const amt = Number(form.amount_uyu) || 0;
  const cardDigits = onlyDigits(form.card_number);
  const phoneDigits = onlyDigits(form.phone);

  // Customer-facing math: prefer the DIRECT funding→dest rate the
  // CEO/Proveedor CU set in /admin/tasas-cambio (e.g. 1 UYU = 15.5 CUP).
  // No commission/profit deducted from the beneficiary side — Aircom
  // margin lives in the proveedor's USDT spread. The 2-hop bridge math
  // is kept as a fallback (and used by the backend for treasury).
  const direct = useMemo(
    () => (form.currency_to ? rates.find((x) => x.pair === `UYU_${form.currency_to}`) : null),
    [rates, form.currency_to],
  );
  const leg1 = useMemo(() => rates.find((x) => x.pair === 'UYU_USDT'), [rates]);
  const leg2 = useMemo(
    () => (form.currency_to ? rates.find((x) => x.pair === `USDT_${form.currency_to}`) : null),
    [rates, form.currency_to],
  );
  const directRate = direct ? Number(direct.rate) : null;
  const bridgeRate = leg1 && leg2 ? (Number(leg1.rate) * Number(leg2.rate)) : null;
  const effectiveRate = directRate || bridgeRate;
  // Commissions live INTERNALLY now (USDT spread) — customer pays nothing.
  const commissionPct = directRate ? 0 : (leg2 ? Number(leg2.commission_pct || 5) : 5);
  const profitPct = directRate ? 0 : (leg2 ? Number(leg2.profit_pct || 0) : 0);
  const gross = effectiveRate != null ? amt * effectiveRate : 0;
  const commission = +(gross * commissionPct / 100).toFixed(2);
  const profit = +(gross * profitPct / 100).toFixed(2);
  const recibe = +(gross - commission - profit).toFixed(2);

  const bucket = fund?.kpis?.UYU || null;
  const blocked = bucket?.block === true;
  const insufficient = bucket && amt > 0 && amt > bucket.balance;

  const canSubmit =
    form.currency_to &&
    amt >= 500 &&
    cardDigits.length >= 8 &&
    !blocked &&
    !insufficient &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        type: 'remittance',
        amount: amt,
        currency_from: 'UYU',
        currency_to: form.currency_to,
        metadata: {
          is_quick: true,
          delivery_method: 'card',
          card_number: cardDigits,
          card_last4: cardDigits.slice(-4),
          beneficiary_phone: phoneDigits || null,
          funding_mode: isAdmin ? 'admin_direct' : 'gestor_balance',
          funding_currency: 'UYU',
          funding_amount: amt,
          rate_pair: `UYU_${form.currency_to}`,
          rate_locked: effectiveRate,
          recibe_aproximado: recibe,
          ganancia: profit,
          commission_pct: commissionPct,
          profit_pct: profitPct,
        },
      };
      const op = await createOperation(getAuthHeaders, payload);
      toast.success(`Remesa creada · ID ${op.id.slice(0, 8)}… · pendiente de confirmación`);
      navigate(isAdmin ? '/admin/remesas/pendientes' : '/remesas/pendientes');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo crear la remesa'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="quick-remesa-form">
      <Card className="border-0 shadow-md rounded-2xl overflow-hidden bg-teal-50/30">
        {/* Teal accent stripe */}
        <div className="h-1.5 bg-teal-600" />

        <div className="p-6 sm:p-8 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-teal-800">Enviar Remesa</h1>
            <p className="text-sm text-slate-500 mt-1">Complete el formulario para enviar una remesa</p>
          </div>

          <div className="h-px bg-slate-200" />

          {/* Tipo de Remesa */}
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Tipo de Remesa</Label>
            <Select value={form.currency_to} onValueChange={(v) => setForm({ ...form, currency_to: v })}>
              <SelectTrigger className="h-12 bg-white" data-testid="quick-currency-select">
                <SelectValue placeholder="Seleccione tipo" />
              </SelectTrigger>
              <SelectContent>
                {DEST_CURRENCIES.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">Seleccione si quiere transferir a MLC, CUP o USD</p>
          </div>

          {/* Cantidades */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Cantidad a Enviar (UYU)</Label>
              <Input
                type="number"
                min="500"
                step="any"
                value={form.amount_uyu}
                onChange={(e) => setForm({ ...form, amount_uyu: e.target.value })}
                placeholder="0"
                className="h-12 bg-white font-mono"
                data-testid="quick-amount-input"
                required
              />
              {bucket && !isAdmin && (
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> Saldo disponible: <span className="font-mono">{bucket.balance.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</span> UYU
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Cantidad a Recibir</Label>
              <Input
                value={
                  amt > 0 && effectiveRate != null && form.currency_to
                    ? `${recibe.toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${form.currency_to}`
                    : ''
                }
                placeholder="Cantidad a recibir"
                readOnly
                className="h-12 bg-white font-mono"
                data-testid="quick-receive-display"
              />
              <p className="text-xs text-slate-400">Cantidad a recibir en Cuba</p>
            </div>
          </div>

          {(blocked || insufficient) && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2" data-testid="quick-fund-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {blocked ? 'Fondo agotado — declara un depósito desde el Centro Financiero.' : 'Saldo UYU insuficiente.'}
            </div>
          )}

          <div className="h-px bg-slate-200" />

          {/* Extra fields */}
          <p className="text-sm font-semibold text-slate-700">Campos adicionales para Administrador Exterior</p>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Número de teléfono para notificación</Label>
            <div className="relative">
              <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Número para notificaciones"
                className="h-12 bg-white pl-10"
                data-testid="quick-phone-input"
              />
            </div>
            <p className="text-xs text-slate-400">Número de teléfono para enviar notificaciones</p>
          </div>

          {/* Card */}
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Tarjeta temporal</Label>
            <div className="relative">
              <CreditCard className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                inputMode="numeric"
                autoComplete="off"
                value={form.card_number}
                onChange={(e) => setForm({ ...form, card_number: formatCard(e.target.value) })}
                placeholder="Formato: 1234-5678-9012-3456"
                className="h-12 bg-white pl-10 font-mono tracking-wider"
                data-testid="quick-card-input"
                maxLength={19}
                required
              />
            </div>
            <p className="text-xs text-slate-400">Escriba aquí la tarjeta temporal en formato dddd-dddd-dddd-dddd</p>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-14 bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white text-base font-semibold rounded-xl"
            data-testid="quick-submit-btn"
          >
            <Send className="w-5 h-5 mr-2" />
            {busy ? 'Creando…' : 'Enviar Remesa'}
          </Button>
          <p className="text-center text-xs text-slate-400">
            {canSubmit ? 'Listo para enviar' : 'Complete todos los campos requeridos para continuar'}
          </p>
        </div>
      </Card>
    </form>
  );
};

export default QuickRemittanceForm;
